import type { ClinicResponse } from '@/types'

interface UseClinicsOptions {
  countySlug?: string
  citySlug?: string
  isEmergency?: boolean
  is24Hours?: boolean
  openNow?: boolean
  limit?: number
  offset?: number
}

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  total: number
  limit: number
  offset: number
  emergencyTotal?: number
  nonstopTotal?: number
}

export function useClinics(options: Ref<UseClinicsOptions> | UseClinicsOptions) {
  const opts = isRef(options) ? options : ref(options)

  const key = computed(() => {
    const o = opts.value
    return `clinics-${o.countySlug || 'all'}-${o.citySlug || 'all'}-${o.isEmergency}-${o.is24Hours}-${o.openNow}-${o.limit}-${o.offset}`
  })

  return useAsyncData<PaginatedResponse<ClinicResponse>>(
    key,
    () =>
      $fetch('/api/clinics', {
        query: {
          countySlug: opts.value.countySlug,
          citySlug: opts.value.citySlug,
          isEmergency: opts.value.isEmergency,
          is24Hours: opts.value.is24Hours,
          openNow: opts.value.openNow,
          limit: opts.value.limit,
          offset: opts.value.offset,
        },
      }),
    {
      watch: [opts],
    }
  )
}

export function useTopClinics(countySlug?: string, limit: number = 8) {
  const key = computed(() => `top-clinics-${countySlug || 'all'}-${limit}`)

  return useAsyncData<{ success: boolean; data: ClinicResponse[] }>(
    key,
    () =>
      $fetch('/api/clinics/top', {
        query: {
          countySlug,
          limit,
        },
      })
  )
}

export function useEmergencyClinics(countySlug?: string, limit: number = 8) {
  const key = computed(() => `emergency-clinics-${countySlug || 'all'}-${limit}`)

  return useAsyncData<{ success: boolean; data: ClinicResponse[] }>(
    key,
    () =>
      $fetch('/api/clinics/emergency', {
        query: {
          countySlug,
          limit,
        },
      })
  )
}
